import { labelTranslations, learnerTourStepTranslations, globalThemeOptions, primaryColor } from "@/assets/js/settings.js"

const state = {
	session: {
		case: null,
		share_link: null,
		publish_page: null
	},
	page: {
		organization: null,
	},
	sessionNode: null,
	sessionTimers: [],
	// True when in-preview edit mode is turned on.
	previewEditMode: true,
	// True when an image is being actively edited. Used to hide the appearance
	// of other toolbar menus while image editing is happening.
	imageEditModeIsActive: false,
	displayCaptions: false,
	displayAudioTranscripts: false,
	videoIsPlaying: false,
	fullScreenIsActive: false,
	learnerToursSilenced: false,
	continueIsLoading: false,
	submitIsLoading: false,
	submitIsAnalyzing: false,
	continueIsEnabled: false,
	submitIsEnabled: false,
	showContinueBtn: false,
	showSubmitBtn: false,
	continueBtnDisabledExplanation: "",
	submitBtnDisabledExplanation: "",
	submitBtnLabel: "",
	// This is used to prevent videos from replaying after saving panels during preview edit mode.
	preContentVideoComplete: false,
	// Set to true when the current node's layout has been edited by the user.
    // Is set to false when the current node changes.
    nodeLayoutWasEdited: false,
	// The ID of the last item accessed by the user on a publish page.
	lastPageItemId: null,
}

const getters = {
	session: state => {
		return state.session
	},
	sessionCase: state => {
		return state.session.case
	},
	sessionNode: state => {
		return state.sessionNode ? state.sessionNode : null
	},
	sessionCurrentNodeId: state => {
		if (!state.sessionNode) {
			return null
		}
		return state.sessionNode.id
	},
	// A flattened list of all the panels contained in the current node's columns.
	sessionCurrentPanels: state => {
		if (!state.sessionNode || !state.sessionNode.columns) {
			return []
		}
		return state.sessionNode.columns.reduce((acc, column) => acc.concat(column.panels), [])
	},
	sessionCaseString: state => {
		return state.session.case ? state.session.case.public_title : ""
	},
	sessionNodeString: state => {
		return state.sessionNode ? state.sessionNode.number + " - " + state.sessionNode.title : ""
	},
	communityCaseSession: state => {
		if (state.session.case) {
			return !state.session.case.organization
		}
		return false
	},
	sessionTimers: state => {
		return state.sessionTimers
	},
	previewEditMode: state => {
		return state.previewEditMode
	},
	imageEditModeIsActive: state => {
		return state.imageEditModeIsActive
	},
	displayCaptions: state => {
		return state.displayCaptions
	},
	sessionLanguageCode: state => {
		if (state.session.case) {
			return state.session.case.ui_language
		}
		return "en"
	},
	sessionLabelsTranslated: (state, getters) => {
		return labelTranslations[getters.sessionLanguageCode]
	},
	learnerTourStepTranslations: (state, getters) => {
		return learnerTourStepTranslations[getters.sessionLanguageCode]
	},
	sessionNotes: state => {
		return state.session.notes_content
	},
	sessionThemePrimaryColor: state => {
		if (state.session.case.global_theme_color) {
			return globalThemeOptions[state.session.case.global_theme_color].primary
		}
		return primaryColor
	},
	videoIsPlaying: state => {
		return state.videoIsPlaying
	},
	fullScreenIsActive: state => {
		return state.fullScreenIsActive
	},
	learnerToursSilenced: state => {
		return state.learnerToursSilenced
	},
	preContentVideoComplete: state => {
		return state.preContentVideoComplete
	},
	continueBtnText: (state, getters) => {
		if (!state.sessionNode || !state.sessionNode.continue_button_text || state.sessionNode.continue_button_text === "") {
			return getters.sessionLabelsTranslated.continue
		}
		return state.sessionNode.continue_button_text
	},
	continueIsLoading: state => {
		return state.continueIsLoading
	},
	submitIsLoading: state => {
		return state.submitIsLoading
	},
	continueIsEnabled: state => {
		return state.continueIsEnabled
	},
	submitIsEnabled: state => {
		return state.submitIsEnabled
	},
	atEndNode: state => {
		if (!state.sessionNode) {
			return false
		}
		return state.sessionNode.end_node || state.sessionNode.final_node
	},
	showContinueBtn: state => {
		return state.showContinueBtn
	},
	showSubmitBtn: state => {
		return state.showSubmitBtn
	},
	submitBtnLabel: state => {
		return state.submitBtnLabel
	},
	submitIsAnalyzing: state => {
		return state.submitIsAnalyzing
	},
	displayAudioTranscripts: state => {
		return state.displayAudioTranscripts
	},
	// Returns the custom logo associated with an active Page or Case, if one exists.
	customLogoUrl: state => {
		let activeOrganization = null
		if (state.session.case && state.session.case.organization) {
			activeOrganization = state.session.case.organization
		} else {
			activeOrganization = state.page.organization
		}
		if (!activeOrganization) return undefined
		if (!activeOrganization.ff_custom_branding || !activeOrganization.logo_image) return null
		return activeOrganization.logo_image.signed_url
	},
	// Prevent videos from auto-playing after a node's layout is edited.
	preventVideoAutoplay: state => {
        return state.nodeLayoutWasEdited
    },
	lastPageItemId: state => {
		return state.lastPageItemId
	},
	continueBtnDisabledExplanation: state => {
		return state.continueBtnDisabledExplanation
	},
	submitBtnDisabledExplanation: state => {
		return state.submitBtnDisabledExplanation
	},
}

const mutations = {
	setSession: (state, session) => {
		// Set the default state of captions if the case has changed.
		if (state.session.case == null || state.session.case.id !== session.case.id) {
			state.displayCaptions = session.case.display_captions_by_default
		}
		let isNewSession = state.session.id != session.id
		let isNewNode = !state.sessionNode || (state.sessionNode.id != session.current_node.id)
		if (isNewSession || isNewNode) {
			state.preContentVideoComplete = false
		}
		state.session = session
	},
	setSessionNode: (state, sessionNode) => {
		state.sessionNode = sessionNode
		state.nodeLayoutWasEdited = false
	},
	setSessionUsername: (state, username) => {
		state.session.anonymous_username = username
	},
	setSessionTimers: (state, sessionTimers) => {
		state.sessionTimers = sessionTimers
	},
	clearSession: (state) => {
		state.sessionTimers = []
		state.session = {
			case: null,
			share_link: null,
		}
	},
	setPreviewEditMode: (state, value) => {
		state.previewEditMode = value
	},
	setImageEditModeIsActive: (state, value) => {
		state.imageEditModeIsActive = value
	},
	setDisplayCaptions: (state, value) => {
		state.displayCaptions = value
	},
	setSessionScore: (state, score) => {
		state.session.score = score
	},
	setSessionNotes: (context, notes) => {
		state.session.notes_content = notes
	},
	setGlobalCaseStyles: (context, settings) => {
		state.session.case.global_theme_color = settings.global_theme_color
		state.session.case.global_font_family_name = settings.global_font_family_name
	},
	setVideoIsPlaying: (state, isActive) => {
		state.videoIsPlaying = isActive
	},
	setFullScreenIsActive: (state, isActive) => {
		state.fullScreenIsActive = isActive
	},
	setLearnerToursSilenced: (state, value) => {
		state.learnerToursSilenced = value
	},
	setPreContentVideoComplete: (state, value) => {
		state.preContentVideoComplete = value
	},
	setContinueIsLoading: (state, value) => {
		state.continueIsLoading = value
	},
	setSubmitIsLoading: (state, value) => {
		state.submitIsLoading = value
	},
	setContinueIsEnabled: (state, value) => {
		state.continueIsEnabled = value
	},
	setSubmitIsEnabled: (state, value) => {
		state.submitIsEnabled = value
	},
	setShowContinueBtn: (state, value) => {
		state.showContinueBtn = value
	},
	setShowSubmitBtn: (state, value) => {
		state.showSubmitBtn = value
	},
	setContinueBtnDisabledExplanation: (state, content) => {
		state.continueBtnDisabledExplanation = content
	},
	setSubmitBtnDisabledExplanation: (state, content) => {
		state.submitBtnDisabledExplanation = content
	},
	setDisplayAudioTranscripts: (state, value) => {
		state.displayAudioTranscripts = value
	},
	setPage: (state, page) => {
		state.page = page
	},
	setNodeLayoutWasEdited: (state, wasEdited) => {
        state.nodeLayoutWasEdited = wasEdited
    },
	setLastPageItemId: (state, itemId) => {
		state.lastPageItemId = itemId
	},
	setSubmitBtnLabel: (state, label) => {
		state.submitBtnLabel = label
	},
	setSubmitIsAnalyzing: (state, isLoading) => {
		state.submitIsAnalyzing = isLoading
	},
}

const actions = {
	setSession: (context, session) => {
		context.commit('setSession', session)
	},
	setSessionNode: (context, sessionNode) => {
		context.commit('setSessionNode', sessionNode)
	},
	clearSession: (context) => {
		context.commit('clearSession')
	},
	toggleDisplayCaptions: (context) => {
		context.commit('setDisplayCaptions', !context.state.displayCaptions)
	},
	setDisplayCaptions: (context, state) => {
		context.commit('setDisplayCaptions', state)
	},
}

export default {
	state,
	getters,
	mutations,
	actions
}
